/**
 * エージェント協調 UI/UX — 型定義
 *
 * effective-typescript 原則:
 * - タグ付きユニオンで不正状態を型レベルで排除
 * - enum 不使用 → union type
 * - Record 型で網羅性保証
 * - readonly で不変性を保証
 * - any 排除
 */

import type { FunctionType } from './index';

// ── SRE ドメイン（6専門領域）──────────────────────────
// enum 不使用: 文字列リテラルのユニオン型で構造的型付けと整合
export type SreDomain =
  | 'security'
  | 'cost'
  | 'performance'
  | 'iam'
  | 'log'
  | 'integrated';

// ── エージェント状態 ──────────────────────────────────
// 各エージェントの実行状態を表す union type
export type AgentStatusKind = 'idle' | 'running' | 'completed' | 'error';

export interface AgentInfo {
  readonly id: string;
  readonly name: string;
  readonly domain: SreDomain;
  readonly status: AgentStatusKind;
  readonly output?: string;
  readonly startedAt?: Date;
  readonly completedAt?: Date;
}

// ── 処理フィードバック（タグ付きユニオン）──────────────
// 不正状態を型レベルで排除:
//   stage='received' に progress は存在しない
//   stage='error' に deliverables は存在しない
export type ProcessingState =
  | { readonly stage: 'idle' }
  | { readonly stage: 'received' }
  | {
      readonly stage: 'processing';
      readonly progress: number;
      readonly activeAgents: readonly string[];
    }
  | {
      readonly stage: 'completed';
      readonly deliverables: readonly DeliverableItem[];
    }
  | {
      readonly stage: 'error';
      readonly error: ProcessingError;
    };

export type DeliverableIconType = 'doc' | 'chart' | 'bulb';

export interface DeliverableItem {
  readonly name: string;
  readonly detail: string;
  readonly iconType: DeliverableIconType;
}

export interface ProcessingError {
  readonly message: string;
  readonly reason: string;
}

// ── アクションカード ──────────────────────────────────
export type ActionType = 'terraform' | 'command' | 'runbook' | 'manual';
export type ImpactLevel = 'high' | 'medium' | 'low';

export interface ActionItem {
  readonly id: string;
  readonly title: string;
  readonly type: ActionType;
  readonly impact: ImpactLevel;
  readonly description: string;
  readonly commands?: readonly string[];
}

// ── ディスカッションステータス ────────────────────────
export interface InsightItem {
  readonly agentName: string;
  readonly content: string;
  readonly messageId?: string;
}

export interface RecommendedAction {
  readonly id: string;
  readonly title: string;
  readonly domain: SreDomain;
  readonly taskType: FunctionType;
}

// ── シナリオカード ────────────────────────────────────
export type ScenarioId =
  | 'security_alert'
  | 'cost_optimization'
  | 'performance_diagnosis'
  | 'iam_compliance'
  | 'log_investigation';

export interface ScenarioCard {
  readonly id: ScenarioId;
  readonly title: string;
  readonly description: string;
  readonly domain: SreDomain;
  readonly samplePrompt: string;
}

// ── ドメインビジュアル設定 ────────────────────────────
export interface DomainVisual {
  readonly label: string;
  readonly color: string;
  readonly bgColor: string;
  readonly lightColor: string;
}

// ── FunctionType ↔ SreDomain のマッピング ──────────────
// Record 型で全 FunctionType の網羅性を保証
export const FUNCTION_TO_DOMAIN: Record<FunctionType, SreDomain> = {
  security_alert: 'security',
  cost_analysis: 'cost',
  performance_diagnosis: 'performance',
  iam_audit: 'iam',
  log_analysis: 'log',
  integrated_report: 'integrated',
};

// ── SSE チャンク拡張型 ────────────────────────────────
// 既存の SseChunk を拡張してエージェント状態・進捗を受信
export type AgentSseChunkType =
  | 'progress'
  | 'agent_status'
  | 'discussion_update'
  | 'action_proposal';

export interface AgentSseChunk {
  readonly type: AgentSseChunkType;
  readonly progress?: number;
  readonly activeAgents?: readonly string[];
  readonly agentStatuses?: readonly AgentInfo[];
  readonly insights?: readonly InsightItem[];
  readonly recommendedActions?: readonly RecommendedAction[];
  readonly actions?: readonly ActionItem[];
}
